import { useState } from "react";
import { Link } from "wouter";
import { useListEvents } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CreditCard, Wallet } from "lucide-react";
import { motion } from "framer-motion";
import { useInfiniteItems } from "@/hooks/useInfiniteItems";
import ismDiamond from "@assets/IMG_3411_1779819410701.png";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useWallet } from "@/hooks/useWallet";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

const EVENT_CATEGORIES = [
  { value: "all",        label: "All"        },
  { value: "film",       label: "Film"       },
  { value: "film festival", label: "Film Festival" },
  { value: "cypher",     label: "Cypher"     },
  { value: "business",   label: "Business"   },
  { value: "concert",    label: "Concert"    },
  { value: "meetup",     label: "Meetup"     },
  { value: "exhibition", label: "Exhibition" },
  { value: "gaming",     label: "Gaming"     },
  { value: "fashion",    label: "Fashion"    },
  { value: "sports",     label: "Sports"     },
];

const TICKET_FILTERS = [
  { value: "all",  label: "All Tickets" },
  { value: "free", label: "Free"        },
  { value: "paid", label: "Paid"        },
];

const SORT_OPTIONS = [
  { value: "asc",  label: "Soonest" },
  { value: "desc", label: "Latest"  },
];

export default function Events() {
  const [category, setCategory]       = useState<string>("all");
  const [upcoming, setUpcoming]       = useState<boolean>(true);
  const [sortOrder, setSortOrder]     = useState<"asc" | "desc">("asc");
  const [ticketFilter, setTicketFilter] = useState<"all" | "free" | "paid">("all");

  const { toast } = useToast();
  const { isConnected } = useWallet();
  const { startCheckout, isLoading: checkoutLoading } = useStripeCheckout();

  const { data: events, isLoading } = useListEvents({ upcoming });

  const filteredEvents = events
    ?.filter(e =>
      (category === "all" || e.category === category) &&
      (ticketFilter === "all" || (ticketFilter === "free" ? !e.ticketPrice : !!e.ticketPrice))
    )
    ?.sort((a, b) =>
      sortOrder === "asc"
        ? new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        : new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    );

  const { visibleItems, hasMore, loadMore, total } = useInfiniteItems(filteredEvents, 6);

  const handleRSVP = (event: typeof visibleItems[number]) => {
    if (!event.ticketPrice) {
      toast({ title: "RSVP Confirmed", description: "You're on the list — see you there!" });
      return;
    }
    if (isConnected) {
      toast({
        title: "Transaction Initiated",
        description: `Approve the payment in your wallet to secure your ticket to ${event.title}.`,
      });
    } else {
      startCheckout({
        productName: event.title,
        productType: "event_ticket",
        amountUsd: event.ticketPrice,
        metadata: { eventId: String(event.id), spaceName: event.spaceName },
      });
    }
  };

  const chipClass = (active: boolean) =>
    `whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-mono border transition-colors ${
      active
        ? "bg-primary/20 border-primary text-primary"
        : "border-white/10 text-muted-foreground hover:border-white/30 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-background text-foreground pb-24 relative overflow-hidden">
      <div className="absolute top-0 right-0 opacity-10 blur-md pointer-events-none z-0">
        <img src={ismDiamond} alt="Background" className="w-[800px] h-[800px] object-contain" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16">
        {/* Title */}
        <div className="text-center space-y-4 mb-12">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase">Virtual Events</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">Attend live concerts, exhibitions, and meetups across the metaverse.</p>
        </div>

        {/* ── Filters ── */}
        <div className="mb-10 bg-card/40 p-4 rounded-xl border border-border backdrop-blur-md space-y-3">

          {/* Row 1: Upcoming / All Events + Sort */}
          <div className="flex gap-2 flex-wrap items-center">
            <Button
              variant={upcoming ? "default" : "outline"}
              onClick={() => setUpcoming(true)}
              className={upcoming ? "bg-primary text-white" : "border-white/20"}
              data-testid="btn-filter-upcoming"
            >
              Upcoming
            </Button>
            <Button
              variant={!upcoming ? "default" : "outline"}
              onClick={() => setUpcoming(false)}
              className={!upcoming ? "bg-primary text-white" : "border-white/20"}
              data-testid="btn-filter-past"
            >
              All Events
            </Button>

            {/* Sort controls — pushed to right */}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-mono">Sort:</span>
              {SORT_OPTIONS.map(s => (
                <button
                  key={s.value}
                  onClick={() => setSortOrder(s.value as "asc" | "desc")}
                  className={chipClass(sortOrder === s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: Category chips */}
          <div className="overflow-x-auto pb-1 no-scrollbar">
            <div className="flex gap-2 w-max">
              {EVENT_CATEGORIES.map(c => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  className={chipClass(category === c.value)}
                  data-testid={`filter-event-${c.value}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Row 3: Ticket filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono">Tickets:</span>
            {TICKET_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setTicketFilter(f.value as "all" | "free" | "paid")}
                className={chipClass(ticketFilter === f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Grid ── */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-[400px] bg-card/50 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visibleItems.map((event, idx) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (idx % 6) * 0.08 }}
                >
                  <Card className="overflow-hidden border-border bg-black/40 hover:bg-black/60 hover:border-primary/50 transition-all duration-300 group flex flex-col h-full backdrop-blur-sm">
                    <div className="relative h-48 w-full">
                      <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {event.live && (
                        <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded text-xs font-bold uppercase tracking-widest animate-pulse">
                          LIVE NOW
                        </div>
                      )}
                      <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-md px-3 py-1 rounded text-xs font-mono border border-white/10 text-primary">
                        {event.category}
                      </div>
                    </div>
                    <CardContent className="p-6 flex-1 flex flex-col">
                      <div className="mb-4">
                        <Link href={`/events/${event.id}`}>
                          <h3 className="font-bold text-2xl uppercase tracking-tight mb-2 leading-tight hover:text-primary transition-colors cursor-pointer">{event.title}</h3>
                        </Link>
                        <div className="text-sm text-muted-foreground font-mono space-y-1">
                          <p>{format(new Date(event.startTime), "MMM do, yyyy • HH:mm")}</p>
                          <p className="text-white/70">@ {event.spaceName}</p>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground mb-6 line-clamp-2 flex-1">{event.description}</p>

                      <div className="pt-4 border-t border-white/10 flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          {event.hostAvatar ? (
                            <img src={event.hostAvatar} alt={event.hostName} className="w-8 h-8 rounded-full border border-white/20" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xs">
                              {event.hostName.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="text-xs">
                            <p className="text-muted-foreground uppercase text-[10px] tracking-wider">Host</p>
                            <p className="font-bold">{event.hostName}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-muted-foreground uppercase text-[10px] tracking-wider">Attendees</p>
                          <p className="font-mono text-sm">{event.attendeeCount}{event.maxAttendees ? `/${event.maxAttendees}` : ""}</p>
                        </div>
                      </div>

                      <Button
                        onClick={() => handleRSVP(event)}
                        disabled={checkoutLoading}
                        className="w-full bg-primary hover:bg-primary/80 font-bold uppercase tracking-widest"
                        data-testid={`btn-rsvp-${event.id}`}
                      >
                        {event.ticketPrice ? (
                          isConnected
                            ? <><Wallet className="w-4 h-4 mr-2" />Get Ticket · {event.ticketPrice} {event.currency}</>
                            : <><CreditCard className="w-4 h-4 mr-2" />Get Ticket · ${event.ticketPrice}</>
                        ) : "RSVP (Free)"}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}

              {filteredEvents?.length === 0 && (
                <div className="col-span-full text-center py-24 text-muted-foreground">
                  <p className="text-xl">No events found for this filter.</p>
                </div>
              )}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3">
              {hasMore && (
                <Button
                  onClick={loadMore}
                  variant="outline"
                  className="border-white/20 hover:border-primary hover:text-primary font-mono uppercase tracking-widest px-8"
                >
                  Load More Events
                </Button>
              )}
              {!hasMore && total > 0 && (
                <p className="text-xs text-muted-foreground font-mono">Showing all {total} event{total !== 1 ? "s" : ""}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
